import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_URLS } from '../config/api.urls';
import { ShippingPricingRequest, ShippingPricingResponse, ShippingPricingItem } from '../_models/shipping-pricing-response';

@Injectable({
    providedIn: 'root'
})
export class DeliveryService {
    private readonly STORAGE_KEY = 'delivery_shipping_selections';
    private readonly LOCAL_PICKUP_KEY = 'delivery_local_pickup';
    private selectedPricings = new Map<number, ShippingPricingItem>();
    private localPickupSelections = new Set<number>();

    constructor(private httpClient: HttpClient) {
        this.loadFromStorage();
    }

    getShippingPricing(request: ShippingPricingRequest): Observable<ShippingPricingResponse> {
        return this.httpClient.post<ShippingPricingResponse>(API_URLS.ORDER_SHIPPING_PRICING, request);
    }

    setSelection(offerId: number, pricing: ShippingPricingItem): void {
        this.selectedPricings.set(offerId, pricing);
        this.localPickupSelections.delete(offerId);
        this.saveToStorage();
    }

    setLocalPickup(offerId: number, selected: boolean): void {
        if (selected) {
            this.localPickupSelections.add(offerId);
            this.selectedPricings.delete(offerId);
        } else {
            this.localPickupSelections.delete(offerId);
        }
        this.saveToStorage();
    }

    isLocalPickupSelected(offerId: number): boolean {
        return this.localPickupSelections.has(offerId);
    }

    getSelection(offerId: number): ShippingPricingItem | undefined {
        return this.selectedPricings.get(offerId);
    }

    clearSelections(): void {
        this.selectedPricings.clear();
        this.localPickupSelections.clear();
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.removeItem(this.LOCAL_PICKUP_KEY);
    }

    hasSelection(offerId: number): boolean {
        return this.selectedPricings.has(offerId) || this.localPickupSelections.has(offerId);
    }

    private saveToStorage(): void {
        const data: { [key: string]: ShippingPricingItem } = {};
        this.selectedPricings.forEach((pricing, offerId) => {
            data[offerId] = pricing;
        });
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        localStorage.setItem(this.LOCAL_PICKUP_KEY, JSON.stringify([...this.localPickupSelections]));
    }

    private loadFromStorage(): void {
        const raw = localStorage.getItem(this.STORAGE_KEY);
        if (raw) {
            try {
                const data: { [key: string]: ShippingPricingItem } = JSON.parse(raw);
                Object.entries(data).forEach(([offerIdStr, pricing]) => {
                    this.selectedPricings.set(parseInt(offerIdStr, 10), pricing);
                });
            } catch (e) {
                // corrupt data, ignore
            }
        }
        const pickupRaw = localStorage.getItem(this.LOCAL_PICKUP_KEY);
        if (pickupRaw) {
            try {
                const ids: number[] = JSON.parse(pickupRaw);
                ids.forEach(id => this.localPickupSelections.add(id));
            } catch (e) {
                // corrupt data, ignore
            }
        }
    }
}
